import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { submitAssessment } from "@/lib/api";

const concerns = [
  "Leash pulling", "Aggression / reactivity", "Jumping on people", "Excessive barking",
  "Separation anxiety", "Puppy basics", "Off-leash reliability", "Recall / coming when called",
  "Resource guarding", "Fearfulness / anxiety", "Service-dog task training", "Other",
];

const urgencies = ["This week", "Within 2 weeks", "This month", "Just exploring"];
const services = ["Phone Consultation", "In-Home Evaluation", "Private Training", "Boot Camp", "Behavioral Rehab", "Service Dog Training", "Not sure yet"];

export default function Assessment({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    dog_name: "", breed: "", age: "",
    concern: concerns[0], urgency: urgencies[0],
    preferred_service: services[0], preferred_contact: "Phone",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setLoading(true);
    try {
      await submitAssessment(form);
      setDone(true);
      toast.success("Assessment received. Toriano's team will contact you shortly.");
    } catch { toast.error("Something went wrong. Please call 312-933-1528."); }
    finally { setLoading(false); }
  };

  const reset = () => { setStep(1); setDone(false); onClose(); };

  const recommendedService = () => {
    if (form.concern.includes("Aggression") || form.concern.includes("Resource")) return "Behavioral Rehabilitation Boot Camp";
    if (form.concern.includes("Puppy")) return "Puppy Boot Camp";
    if (form.concern.includes("Service")) return "Service Dog Training";
    if (form.urgency === "This week") return "In-Home Evaluation";
    return "Private Dog Training";
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-ink/85 backdrop-blur-lg overflow-y-auto"
          onClick={reset}
          data-testid="assessment-modal"
        >
          <div className="min-h-full flex items-center justify-center p-4 py-16">
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22,1,0.36,1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl bg-ink-2 border border-gold/30 p-8 md:p-10 relative"
            >
              <button onClick={reset} className="absolute top-4 right-4 text-bone/60 hover:text-gold" data-testid="assessment-close">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>

              {done ? (
                <div>
                  <div className="text-eyebrow text-gold mb-4">Recommendation</div>
                  <h3 className="font-display text-4xl md:text-5xl italic gold-foil leading-tight">{recommendedService()}</h3>
                  <p className="mt-5 text-bone/75 font-editorial text-lg">Based on your answers, this is where we'd start. Toriano's team will reach out via {form.preferred_contact.toLowerCase()} within 24 hours to schedule your evaluation.</p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <a href="tel:3129331528" className="btn-gold" data-testid="assessment-call">Call 312-933-1528</a>
                    <button onClick={reset} className="btn-outline" data-testid="assessment-done">Close</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-eyebrow text-gold mb-2">Free · 3-minute assessment</div>
                  <h3 className="font-display text-3xl md:text-4xl leading-tight">Is your dog trained — or has your dog <span className="italic gold-foil">trained you?</span></h3>
                  <div className="mt-3 text-hair text-bone/50">Step {step} of 3</div>

                  {step === 1 && (
                    <div className="mt-6 grid md:grid-cols-2 gap-5">
                      <div className="field"><label>Your name</label><input required value={form.name} onChange={upd("name")} data-testid="asm-name" /></div>
                      <div className="field"><label>Phone</label><input required value={form.phone} onChange={upd("phone")} data-testid="asm-phone" /></div>
                      <div className="field md:col-span-2"><label>Email</label><input required type="email" value={form.email} onChange={upd("email")} data-testid="asm-email" /></div>
                      <div className="md:col-span-2 flex justify-end">
                        <button onClick={() => setStep(2)} className="btn-gold" data-testid="asm-next-1" disabled={!form.name || !form.phone || !form.email}>Continue →</button>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="mt-6 grid md:grid-cols-2 gap-5">
                      <div className="field"><label>Dog's name</label><input value={form.dog_name} onChange={upd("dog_name")} data-testid="asm-dog" /></div>
                      <div className="field"><label>Breed</label><input value={form.breed} onChange={upd("breed")} data-testid="asm-breed" /></div>
                      <div className="field md:col-span-2"><label>Age</label><input value={form.age} onChange={upd("age")} placeholder="e.g. 8 months / 3 years" data-testid="asm-age" /></div>
                      <div className="field md:col-span-2"><label>Primary behavior concern</label>
                        <select value={form.concern} onChange={upd("concern")} data-testid="asm-concern">
                          {concerns.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="md:col-span-2 flex justify-between">
                        <button onClick={() => setStep(1)} className="btn-outline" data-testid="asm-back-2">← Back</button>
                        <button onClick={() => setStep(3)} className="btn-gold" data-testid="asm-next-2">Continue →</button>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="mt-6 grid md:grid-cols-2 gap-5">
                      <div className="field"><label>How urgent is this?</label>
                        <select value={form.urgency} onChange={upd("urgency")} data-testid="asm-urgency">{urgencies.map(u => <option key={u}>{u}</option>)}</select>
                      </div>
                      <div className="field"><label>Preferred service</label>
                        <select value={form.preferred_service} onChange={upd("preferred_service")} data-testid="asm-service">{services.map(s => <option key={s}>{s}</option>)}</select>
                      </div>
                      <div className="field md:col-span-2"><label>Preferred contact method</label>
                        <select value={form.preferred_contact} onChange={upd("preferred_contact")} data-testid="asm-contact-method">
                          <option>Phone</option><option>Text</option><option>Email</option>
                        </select>
                      </div>
                      <div className="md:col-span-2 flex justify-between">
                        <button onClick={() => setStep(2)} className="btn-outline" data-testid="asm-back-3">← Back</button>
                        <button onClick={submit} disabled={loading} className="btn-gold" data-testid="asm-submit">
                          {loading ? "Submitting…" : "See my recommendation"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
